import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DataService, DateService, FormatService, HelperService, HTTPService, SocketService } from 'app/services';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components/confirm-dismiss-dialog/confirm-dismiss-dialog.component';
import { mileageUnits, vehicleTransmissions } from 'app/constants';
import { apptSocketEvents } from 'app/constants/socket.constants';

import { find, isEqual, trim } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { currency } from 'app/constants/currency.constants';
import { datetimeFormat } from 'app/constants/date-time-format';
import { IDriver } from '../driver-side-list/driver-side-list.interface';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  providers: [DialogService],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @ViewChild('sidebarContainer', { static: true }) sidebarContainer: ElementRef;
  @Input() estimateId: string = '';

  sideBarVisible: boolean = false;
  isCustomerLoading: boolean = false;
  isVehiclesLoading: boolean = false;
  isLoadingAppointment: boolean = true;
  customerAppts = {
    current: 0,
    appointments:[],
  };
  index: number = 0;
  customer: any = {};
  vehicles: any[] = [];
  defaultCustomer: any = {};
  defaultVehicles: any[] = [];
  transferVehicles: any[] = [];
  shopsMap: any = {};
  tags: any[] = [];
  vehicleColors: any[] = [];
  driveTypes: any[] = [];
  vehicleTags: any[] = [];
  customerData: any;
  clickOutsideListener: any = null;
  isVehicleFetched: boolean = false;
  isCustomerFetched: boolean = false;
  isVehicleUpdated: boolean = false;
  isCustomerUpdated: boolean = false;
  highLightedVehicleId: string = '';
  defaultRegState: string = '';
  defaultRegStateCode: string = '';
  highLightedCustomerNotes: boolean = false;
  displayExternalServiceNotes: boolean = false;
  highLightedVIN: boolean = false;
  highLightedMiles: boolean = false;
  openTaxExemptDropdown: boolean = false;
  openPreferredLocation: boolean = false;
  openConnectMotor: boolean = false;
  vehicleSearchPlaceholder: string = 'Search VIN or year, make, model';
  isCarfaxEnabled: boolean = false;
  isMotorLookupEnabled: boolean = false;
  isTireStorageEnabled: boolean = false;
  isDeferredServiceEnabled: boolean = false;
  isCustomerPaymentEnabled: boolean = false;
  isMarketingEnabled: boolean = false;
  isMultiLocationEnabled: boolean = false;
  isOpenConfirmationDialog: boolean = false;
  vehicleOdmHistory: any = {};
  defaultMileageUnit: string = '';
  qboTaxExemptReasons: any[] = [];
  roOpenAction: string = '';
  isChatEnabled: boolean;
  fetchTags: boolean = true;
  openNotesTab: boolean = false;
  totalRecords: number = 0;
  totalCreditMemoRecords: number = 0;
  isFieldsMissing: boolean = false;
  skip: number = 0;
  creditMemoSkip: number = 0;
  customerId: string = '';
  acFocus: any = {
    title: false,
    notes: false,
    tech: false,
  };
  errors: any = {};
  notesRows: number;
  defaultNotes: string = '';
  notePanelData = {
    type: '',
    notes: '',
    title: '',
    sideModalStyleClass: '',
    subTitle: '',
  };
  contactValidation = {
    email: {
      tooltip: '',
      icon: '',
    },
    phone: {
      tooltip: '',
      icon: '',
    },
  };
  chargeAccounts: any;
  isArAccountPermitted: boolean = false;
  isRevenueRecognitionSettleEnabled: boolean = false;
  isChargeAccountEnabled: boolean = false;
  isMyFleetCenterEnabled: boolean = false;
  creditMemo = [];
  totalAvailableCredit;
  usd: string = currency.USD;
  isTransferVehicleEnabled: boolean = false;
  estimate: any = {};
  financeTab = 1;
  currentShopId: string = '';
  defaultCustomerChargeAccountState: boolean = false;
  isFinalizedRo: boolean = false;
  private unsubscribe: Subject<boolean> = new Subject<boolean>();
  drivers = [];
  vehicleDrivers = [];
  defaultDrivers = [];
  localizationSubscription: Subscription;
  isDriverUpdated: boolean = false;
  companyLocalization: boolean = false;
  selectedDriverId = '';

  constructor(
    private dataService: DataService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private dialogService: DialogService,
    private dateService: DateService,
    private socketService: SocketService,
    private formatService: FormatService,
  ) { }

  ngOnInit() {
    const { isRevenueRecognitionSettleEnabled } = this.helperService.getCompany();
    this.isRevenueRecognitionSettleEnabled = isRevenueRecognitionSettleEnabled;
    this.setFeatureFlag();
    this.onDataReceived();
    this.setClickOutsideListener();
    this.localizationSubscription = this.dataService.getCompanyLocalization().subscribe((res) => {
      this.companyLocalization = res;

      if (this.localizationSubscription) this.localizationSubscription.unsubscribe();
    });
    this.selectedDriverId = '';
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.unsubscribe();
  }

  /**
   * Listen to customer appt. created socket event
   */
  private listenCustomerApptCreatedEvent(shopId: string, customerId: string): void {
    this.socketService
      .socketOn(`${shopId}_${customerId}_${apptSocketEvents.CUSTOMER_APPT_CREATED}`)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ appointmentId }) => this.getCustomerAppointments({ limit: 20, offset: 0, customerId }));
  }

  /** Fetching customer data and vehicle data (if not fetched already) */
  getCustomer(customerId: string) {
    this.isCustomerLoading = true;
    this.httpService.getCustomer(customerId, { fetchTags: true, populatePricingMatrix: true, populateTaxCodeOverRide: true }).then(
      (customer: any) => {
        if (!customer) {
          return;
        }

        this.tags = customer.customerTags || [];
        delete customer.customerTags;
        this.customer = customer;
        if (!this.customer.address) {
          this.customer.address = {};
        }

        if (!this.customer.businessAddress) {
          this.customer.businessAddress = {};
        }

        if (!this.customer.tags) {
          this.customer.tags = [];
        }

        if (customer.reasons && customer.reasons.length) {
          this.qboTaxExemptReasons = customer.reasons;
        }

        if (customer.isFleetCustomer) {
          this.customer.customerType = { label: 'Fleet', value: 'fleet' };
        } else {
          this.customer.customerType = { label: 'Regular', value: 'regular' };
        }

        if (customer.taxExempt && customer.taxExemptReasonId) {
          this.customer.taxExemptReason = find(this.qboTaxExemptReasons, {
            qboId: customer.taxExemptReasonId,
          });
        }

        if (!this.customer.address?.country) this.customer.address.country = this.dataService.company?.address?.country;
        if (!this.customer.businessAddress?.country) this.customer.businessAddress.country = this.dataService.company?.address?.country;

        if (!this.isVehicleFetched) {
          this.customerId = customerId;
          this.getVehicle();
          this.isVehicleFetched = true;
        }

        const conf = { limit: 20, offset: 0, customerId: customer._id };
        this.getCustomerAppointments(conf);

        this.defaultCustomer = this.helperService.deepCopy(this.customer);
        this.defaultCustomerChargeAccountState = this.defaultCustomer.chargeAccountEnable;
        this.isCustomerFetched = true;
        this.isCustomerLoading = false;
        this.totalAvailableCredit = this.customer.creditAmount;
        this.setContactValidation();
        if (this.isMyFleetCenterEnabled) {
          this.getAllDrivers();
        }
        this.isFieldsMissing = false;
        if (this.dataService.isCustomerVehicleMandatoryFieldsEnabled) {
          if (this.dataService.customerMandatoryFields.email && !this.customer.email) {
            this.errors.email = '* Email Required';
            this.isFieldsMissing = true;
          }

          if (this.dataService.customerMandatoryFields.mobile && !this.customer.phone) {
            this.errors.phone = '* Phone Required';
            this.isFieldsMissing = true;
          }

          if (this.dataService.customerMandatoryFields.address) {
            this.errors.address = {};
            if (!trim(this.customer.address?.addLine1)) {
              this.errors.address.addLine1 = 'Please enter the Address.';
              this.isFieldsMissing = true;
            }

            if (!trim(this.customer.address?.zip)) {
              this.errors.address.zip = 'Please enter the Zip Code.';
              this.isFieldsMissing = true;
            }

            if (!trim(this.customer.address?.city)) {
              this.errors.address.city = 'Please select a City.';
              this.isFieldsMissing = true;
            }

            if (!trim(this.customer.address?.state)) {
              this.errors.address.state = 'Please select a State/Province.';
              this.isFieldsMissing = true;
            }

            if (!trim(this.customer.address?.country)) {
              this.errors.address.country = 'Please select a Country.';
              this.isFieldsMissing = true;
            }

            if (this.isFieldsMissing) {
              this.dataService.sendData(this.dataService.typePassData('check-validations', customer));
            }
          }
        }
      },
      (err) => {
        if (err && err.isCustomerNotFound) {
          this.dataService.sendData(this.dataService.typeSidebar('close', null, null));

          this.helperService.showToaster('error', err.message);
        } else {
          this.helperService.showToaster('error', err);
        }

        this.isCustomerLoading = false;
      },
    );
  }

  /** Getting customer vehicles */
  getVehicle() {
    if (this.isVehiclesLoading) return;

    this.isVehiclesLoading = true;

    const queryParams: any = {
      limit: 20,
      skip: this.skip,
      fetchTags: this.fetchTags,
    };

    if (!this.skip) {
      queryParams.vehicleId = this.highLightedVehicleId;
    }
    if (this.isMyFleetCenterEnabled) {
      this.getAllDrivers();
    }

    this.httpService
      .getCustomerVehicle(this.customerId, queryParams)
      .then(({ vehicles, totalRecords, tags, company }) => {
        if (this.fetchTags) {
          this.setVehicleTags(tags || []);
          this.setCompanySettings(company);
        }

        const _vehicles = vehicles;
        this.skip += 20;
        this.totalRecords = totalRecords;
        this.vehicles = [...this.vehicles, ...this.formatVehicles(vehicles)];
        // this.defaultVehicles = [...this.vehicles, ...this.formatVehicles(vehicles)];
        this.defaultVehicles = this.helperService.deepCopy(this.vehicles);
      })
      .finally(() => {
        this.isVehiclesLoading = false;
      });
  }

  // getAllVehicles(customerId: string, fetchTags: boolean = false) {
  //   this.isVehiclesLoading = true;
  //   this.httpService.getVehiclesByCustomer(customerId, fetchTags).then(
  //     (data: any) => {
  //       this.isVehiclesLoading = false;

  //       if (!data) {
  //         return;
  //       }

  //       const { tags, company, vehicles } = data;
  //       const { appsettings, address } = company || {};

  //       if (tags && tags.length && fetchTags) {
  //         this.setVehicleTags(tags);
  //       }

  //       if (appsettings && appsettings.carfaxConnected) {
  //         this.isCarfaxEnabled = appsettings.carfaxConnected;
  //         this.vehicleSearchPlaceholder = 'Search license plate, VIN or YMM';
  //       }

  //       if (appsettings && appsettings.mileageMeasuringUnit) {
  //         this.defaultMileageUnit = appsettings.mileageMeasuringUnit;
  //       } else if (address && address.country === 'Canada') {
  //         this.defaultMileageUnit = 'km';
  //       } else if (address && address.country === 'US') {
  //         this.defaultMileageUnit = 'mi';
  //       }

  //       if (address && address.state) {
  //         this.defaultRegState = address.state;
  //         this.defaultRegStateCode = address.stateCode;
  //       }

  //       this.vehicles = this.formatVehicles(vehicles);
  //       this.defaultVehicles = this.helperService.deepCopy(this.vehicles);
  //     },
  //     (err) => {
  //       this.isVehiclesLoading = false;
  //       this.helperService.showToaster('error', err);
  //     },
  //   );
  // }

  formatVehicles(vehicles: any[]) {
    return vehicles.map((vehicle) => {
      const { ODM, VIN, year, color, vehicleName, driveType, transmission, connectedVia, isCarfaxConnected, name } = vehicle;

      vehicle.isCarfaxConnected = isCarfaxConnected;
      vehicle.connectedVia = connectedVia;
      vehicle.color = { name: color || '' };
      vehicle.driveType = { name: driveType || '' };
      vehicle.vin = VIN || '';
      vehicle.year = `${year || ''}`;
      vehicle.vehicleName = '';
      if (VIN) {
        vehicle.defaultVin = this.helperService.deepCopy(VIN);
      }
      // if (this.customer.isFleetCustomer) {
      vehicle.vehicleName = vehicleName || name || '';
      // }

      vehicle.odmHistory = [];

      if (vehicle.licensePlateNo && !vehicle.registrationStateCode) {
        vehicle.registrationState = this.defaultRegState;
        vehicle.registrationStateCode = this.defaultRegStateCode;
        vehicle.state = vehicle.registrationState;
      }

      vehicle.transmission = find(vehicleTransmissions, { value: transmission }) || {};

      if (ODM && ODM.length) {
        this.vehicleOdmHistory[vehicle._id] = ODM.map((odm: any) => ({
          ...odm,
          readingDate: this.dateService.getCurrentDateTimeTz(new Date(odm.readingDate).getTime()),
        }));

        // let { reading, mileageMeasuringUnit } = ODM.pop();
        let { reading, mileageMeasuringUnit } = ODM[ODM.length - 1];

        vehicle.mileage = `${reading || ''}`;

        if (reading) {
          if (!mileageMeasuringUnit) {
            mileageMeasuringUnit = this.defaultMileageUnit;
          }

          vehicle.mileageMeasuringUnit = find(mileageUnits, {
            value: mileageMeasuringUnit,
          });
        }
      }

      if (vehicle.transferDetails && vehicle.transferDetails.vehicleTransferDate) {
        vehicle.transferDetails = {
          ...vehicle.transferDetails,
          vehicleTransferDate: this.dateService.formatDate(vehicle.transferDetails.vehicleTransferDate, datetimeFormat.mmmdyyyy),
        };
      }

      return vehicle;
    });
  }

  setVehicleTags(tags: any[]) {
    this.vehicleColors = [];
    this.driveTypes = [];
    this.vehicleTags = [];

    tags.forEach((tag: any) => {
      if (tag.type === 'vehicle_color') {
        this.vehicleColors.push(tag);
      } else if (tag.type === 'drive_type') {
        this.driveTypes.push(tag);
      } else if (tag.type === 'vehicle') {
        this.vehicleTags.push(tag);
      }
    });

    this.vehicleTags = [...this.vehicleTags];
  }

  setCompanySettings(company: any) {
    if (company) {
      const { appsettings, address } = company;

      if (appsettings) {
        if (appsettings.carfaxConnected) {
          this.isCarfaxEnabled = appsettings.carfaxConnected;
          this.vehicleSearchPlaceholder = 'Search license plate, VIN or YMM';
        }

        if (appsettings.mileageMeasuringUnit) {
          this.defaultMileageUnit = appsettings.mileageMeasuringUnit;
        } else if (address) {
          this.defaultMileageUnit = address.country === 'Canada' ? 'km' : 'mi';
        }

        if (address && address.state) {
          this.defaultRegState = address.state;
          this.defaultRegStateCode = address.stateCode;
        }
      }
    }
  }

  updateCustomerTagList(tags: any[]) {
    this.tags = tags;
  }

  /** Getting customer appointments */
  getCustomerAppointments(customer: any) {
    this.isLoadingAppointment = true;
    this.httpService.getAppointmentsByCustomerId(customer).then(
      ({ data, current }) => {
        this.customerAppts = {
          current: current || 0,
          appointments: data || [],
        };
        this.isLoadingAppointment = false;
      },
      (err) => {
        this.isLoadingAppointment = false;
        this.helperService.showToaster('error', err);
      },
    );
  }

  /** When user clicks outside of the sidebar */
  setClickOutsideListener() {
    this.clickOutsideListener = this.dataService.getClickEvent().subscribe((target) => {
      const sidebarShade = document.querySelector('.p-sidebar-mask');

      if (sidebarShade && sidebarShade.contains(target)) {
        this.onSidebarOutside();
      }
    });
  }

  /** Removing click outside listener */
  handleUnsubscribleClickoutside() {
    if (this.clickOutsideListener) {
      this.clickOutsideListener.unsubscribe();
    }

    this.clickOutsideListener = null;
  }

  /** Listening for data events from other action to fetch data or perform action accordingly */
  onDataReceived() {
    this.dataService
      .getData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ data }) => {
        if (!data) return;

        const { _id, fullName, key, tab, type } = data;

        if (data.key === 'company-appdata-updated') {
          this.setFeatureFlag();
        }

        if (data.key === 'customer-memo-list') {
          if (this.customerId) {
            this.getAllCreditMemo(this.customerId);
            this.getCustomer(this.customerId);
          }
        }

        if (key === 'default-customer-update') {
          const obj = data?.data;
          this.customer = obj.customer;
          this.customer.hasNoChanges = true;
        }

        if (type === 'passdata' && data.key === 'merge-vehicle') {
          this.openNotesTab = false;
          this.fetchTags = true;
          this.skip = 0;
          this.totalRecords = 0;
          this.vehicles = [];
          this.defaultVehicles = [];
          this.sideBarVisible = true;
          this.getVehicle();
          this.getCustomer(data.data.customerId);
        }

        if (key !== 'sidebar-check-customer' && key !== 'sidebar-check-vehicle') {
          if (type === 'sidebar' && tab === 'customer') {
            this.openNotesTab = false;
            const obj = data.data;
            this.estimate = obj?.estimate || {};
            this.index = 0;
            this.highLightedCustomerNotes = obj.action === 'scroll-to-notes';
            this.openTaxExemptDropdown = obj.action && obj.action.includes('tax-exempt-reason');
            this.openPreferredLocation = obj.action === 'preferred-location';
            this.roOpenAction = obj.action;
            this.customer._id = _id;
            this.customer.fullName = fullName;
            this.getCustomer(this.customer._id);
            this.getAllCreditMemo(this.customer._id);

            if (!this.clickOutsideListener) {
              this.setClickOutsideListener();
            }

            // listen to appointment created socket event
            this.listenCustomerApptCreatedEvent(this.helperService.getCompany().shopId, _id);
            this.fetchTags = true;
            this.skip = 0;
            this.totalRecords = 0;
            this.vehicles = [];
            this.defaultVehicles = [];
            this.sideBarVisible = true;
            this.setSidebarZIndex();
            if (obj.action === 'select-finance') {
              setTimeout(() => {
                this.handleTabChange({ index: 5 });
                this.handleFinanceTab(2);
              }, 1500);
            }
            if (type === 'sidebar' && obj.action === 'chargeAccount') {
              setTimeout(() => {
                this.customer.chargeAccountEnable = true;
                if (this.customer._id) {
                  this.openConfirmationDialog();
                }
              }, 1500);
            }
          } else if (type === 'sidebar' && tab === 'vehicle') {
            this.openNotesTab = false;
            if (data.data.type === 'vehicle-add') {
              this.isVehicleFetched = false;
            }
            const obj = data.data;
            this.index = 1;

            if (obj && obj.vehicle && obj.vehicle._id && obj.from === 'vehicle-side-list') {
              // const [newVehicle] = this.formatVehicles([data.data.vehicle]);

              this.vehicles = [];
              this.skip = 0;
              this.getVehicle();
              // this.vehicles.splice(0, 0, this.helperService.deepCopy(newVehicle));
              // this.defaultVehicles.splice(0, 0, this.helperService.deepCopy(newVehicle));
            } else {
              if (obj._id) {
                this.customer._id = obj._id;
                this.openConnectMotor = obj.openConnectMotor;
                this.highLightedVIN = obj.highLightedVIN;
                this.highLightedMiles = obj.highLightedMiles;
                if (obj.isVehicleFetched) {
                  this.isVehicleFetched = !obj.isVehicleFetched;
                }
                this.getCustomer(this.customer._id);
                this.highLightedVehicleId = obj.vehicleId;
              }
              if (obj.zIndex) {
                this.setSidebarZIndex();
              }

              this.fetchTags = true;
              this.skip = 0;
              this.totalRecords = 0;
              this.vehicles = [];
              this.defaultVehicles = [];
            }

            if (!this.clickOutsideListener) {
              this.setClickOutsideListener();
            }

            this.sideBarVisible = true;

            if (this.openConnectMotor && obj.openConflictDialog === false) {
              this.setSidebarZIndex();
            }
            this.getAllCreditMemo(this.customer._id);
          }
          else if (type === 'sidebar' && tab === 'driver') {
            this.isMyFleetCenterEnabled = true;

            this.customer = {
              ...this.customer,
              isFleetCustomer: true,
            };
            const obj = data.data;
            setTimeout(() => {
              this.index = 2;
            }, 0);

            if (obj._id) {
              this.customer._id = obj._id;
              this.selectedDriverId = obj.driverId;
              this.getCustomer(this.customer._id);

            }

            // Set click outside listener if not already set
            if (!this.clickOutsideListener) {
              this.setClickOutsideListener();
            }

            this.sideBarVisible = true;

            // Adjust sidebar z-index if no conflict dialog
            if (this.openConnectMotor && obj.openConflictDialog === false) {
              this.setSidebarZIndex();
            }
          }


          else if (type === 'sidebar' && tab === 'close') {
            this.openNotesTab = false;
            const isVehicleUpdated = isEqual(this.vehicles, this.defaultVehicles);
            const isDriverUpdated = this.getDriverChanges();

            /** Unsubscribe listener on clickoutside */
            if (this.isCustomerUpdated) {
              if (data.data && data.data.type === 'customer' && isVehicleUpdated && isDriverUpdated) {
                this.sideBarVisible = false;
                this.openConnectMotor = false;
                this.highLightedVIN = false;
                this.highLightedMiles = false;
                this.highLightedCustomerNotes = false;
                this.openTaxExemptDropdown = false;
                this.openPreferredLocation = false;
                this.handleResetForm();
                this.handleUnsubscribleClickoutside();
              } else {
                if (!data.data || (data.data && data.data.type !== 'customer') || isVehicleUpdated || isDriverUpdated) {
                  this.sideBarVisible = false;
                  this.openConnectMotor = false;
                  this.highLightedCustomerNotes = false;
                  this.openTaxExemptDropdown = false;
                  this.openPreferredLocation = false;
                  this.handleResetForm();
                  this.handleUnsubscribleClickoutside();
                }
              }
            } else {
              if (!data.data || (data.data && data.data.type !== 'customer') || isVehicleUpdated || isDriverUpdated) {
                this.sideBarVisible = false;
                this.openConnectMotor = false;
                this.highLightedCustomerNotes = false;
                this.openTaxExemptDropdown = false;
                this.openPreferredLocation = false;
                this.handleResetForm();
                this.handleUnsubscribleClickoutside();
              }
            }
          }

          else if (type === 'sidebar' && tab === 'sidebar-notes') {
            this.openNotesTab = true;
            this.sideBarVisible = true;
            this.defaultNotes = this.helperService.deepCopy(data.data.notes);
            this.notePanelData = this.helperService.deepCopy(data.data);
            this.isFinalizedRo = data.data.isFinalizedRo;
          } else if (
            (data.type === 'dialog' && (data.tab === 'estimate' || data.tab === 'sidebar') && data.from === 'appointment') ||
            (data.tab === 'calendar' && data.component === 'calendar-view' && data.data)
          ) {
            this.openNotesTab = false;
            if (this.customer._id && this.sideBarVisible) {
              const conf = {
                limit: 20,
                offset: 0,
                customerId: this.customer._id,
              };
              this.getCustomerAppointments(conf);
            }
          }
        }

        if (key === 'customer-vehicle-list') {
          const obj = data.data;
          this.transferVehicles = obj.vehicles;
        }

        if (type === 'update' && data.component === 'vehile-side-list' && data.tab === 'vehicle') {
          // this.getAllVehicles(this.customer._id || data.data?.customerId);
        }

        if (key === 'sidebar-driver-update') {
          const obj = data.data;
          if (obj._id) {
            // Update the driver with matching employeeNumber
            this.drivers = this.drivers.map((res) => (res.employeeNumber === obj.employeeNumber || !res._id ? { ...res, _id: obj._id } : res));
          }
          // Deep copy the updated drivers to defaultDrivers
          this.defaultDrivers = this.helperService.deepCopy(this.drivers);
        }
      });
  }

  setFeatureFlag() {
    this.isCustomerPaymentEnabled = this.dataService.isCustomerPaymentEnabled;
    this.isDeferredServiceEnabled = this.dataService.isDeferredServiceEnabled;
    this.isChatEnabled = this.dataService.isChatEnabled;
    this.isMarketingEnabled = this.dataService.isMarketingEnabled;
    this.isTireStorageEnabled = this.dataService.isTireStorageEnabled;
    this.displayExternalServiceNotes = this.dataService.displayExternalServiceNotes;
    this.isMultiLocationEnabled = this.dataService.isMultiLocationEnabled;
    this.shopsMap = this.dataService.shopsMap;
    this.isTransferVehicleEnabled = this.dataService.isTransferVehicleEnabled;
    this.chargeAccounts = this.dataService.chargeAccounts;
    this.isArAccountPermitted = this.dataService.isArAccountManagement;
    this.currentShopId = this.dataService.currentShopId;
    this.isChargeAccountEnabled = this.dataService.isChargeAccountEnabled;
    this.isMyFleetCenterEnabled = this.dataService.isMyFleetCenterEnabled;
    //  && this.dataService.isMyFleetCenterConnected;
    this.isMotorLookupEnabled = this.dataService.isMotorLookupEnabled;
  }

  /** Reset data when sidebar is closed */
  handleHideSidebar() {
    this.dataService.sendData(
      this.dataService.typePassData('update-order-customer-close', {
        isSuccess: true,
        customer: this.customer,
        type: 'data',
        roOpenAction: this.roOpenAction,
      }),
    );
    this.roOpenAction = '';
    this.openTaxExemptDropdown = false;
    this.openPreferredLocation = false;
    this.handleResetForm();
    this.handleUnsubscribleClickoutside();
    this.financeTab = 1;
  }

  /** Setting active status of customer */
  handleSwitchChange(type, value: boolean) {
    if (type === 'active') {
      this.customer.isActive = value;
    } else if (type === 'chargeAccount') {
      if (value) {
        this.openConfirmationDialog();
      }
      this.customer.chargeAccountEnable = value;
      this.defaultCustomerChargeAccountState = this.customer.chargeAccountEnable;
    }
  }

  /** Handle tab change in sidebar */
  handleTabChange({ index }) {
    this.index = index;

    switch (index) {
      case 0:
        if (this.customer._id && !this.isCustomerFetched && !this.isCustomerLoading) {
          this.getCustomer(this.customer._id);
        }

        return;

      case 1:
        if (this.customer._id && !this.isVehicleFetched && !this.isVehiclesLoading) {
          this.customerId = this.customer._id;
          this.getVehicle();
        }
        if (this.isMyFleetCenterEnabled) {
          this.getAllDriversForVehicles();
        }
        return;

      case 2:
        // dont need to add extra call of API using exsisting loaded vehicle list
        // this.getAllVehicles(this.customerId, true);
        return;

      case 5:
        this.creditMemoSkip = 0;
        this.creditMemo = [];
        this.getAllCreditMemo(this.customer._id);
        return;
    }
  }

  updateTags(data: any) {
    if (!data) return;

    if (data.type === 'vehicle_color') {
      for (let i = 0; i < this.vehicleColors.length; i++) {
        if (this.vehicleColors[i].type === data.type && data._id === this.vehicleColors[i]._id) {
          this.vehicleColors[i].name = data.name;
          this.vehicleColors = this.helperService.deepCopy(this.vehicleColors);
          break;
        }
      }
    } else if (data.type === 'vehicle') {
      for (let i = 0; i < this.vehicleTags.length; i++) {
        if (this.vehicleTags[i].type === data.type && data._id === this.vehicleTags[i]._id) {
          this.vehicleTags[i].name = data.name;
          this.vehicleTags = this.helperService.deepCopy(this.vehicleTags);

          this.vehicles.forEach((vehicle) => {
            if (vehicle.tags && vehicle.tags.length) {
              vehicle.tags.forEach((tag: any) => {
                if (tag.categoryId === data._id) {
                  tag.name = data.name;
                }
              });
            }
          });
          break;
        }
      }
    } else {
      for (let i = 0; i < this.driveTypes.length; i++) {
        if (this.driveTypes[i].type === data.type && data._id === this.driveTypes[i]._id) {
          this.driveTypes[i].name = data.name;
          this.driveTypes = this.helperService.deepCopy(this.driveTypes);
          break;
        }
      }
    }
  }

  updateTagList({ type, list }) {
    if (type === 'vehicle') {
      this.vehicleTags = [...list];
    }
  }

  handleResetForm() {
    this.index = 0;
    this.customer = {};
    this.vehicles = [];
    this.defaultVehicles = [];
    this.customerAppts = {
      appointments: [],
      current:0,
    };
    this.customerId = null;
    this.isVehicleUpdated = false;
    this.isCustomerUpdated = false;
    this.isCustomerFetched = false;
    this.isVehicleFetched = false;
    this.isDriverUpdated = false;
    this.skip = 0;
    this.totalRecords = 0;
    this.fetchTags = true;
    this.drivers = [];
    this.vehicleDrivers = [];
    this.defaultDrivers = [];
  }

  updateCustomerList(data: any = {}) {
    if (data && data.updateContactOnly) {
      this.customer.email = data.email;
      this.customer.phone = data.phone;
      this.customer.contactOnEmail = data.contactOnEmail;
      this.customer.contactOnPhone = data.contactOnPhone;
    }

    /** This data is subscribe in customer-list.component */
    this.defaultCustomer = this.helperService.deepCopy(this.customer);
    this.dataService.sendData(this.dataService.typeUpdate('customer-list', 'customer'));
  }

  customerDetails(data: any) {
    this.customerData = data;
  }

  getVehicleUpdate(vehicle: any) {
    if (this.customer._id) {
      for (let i = 0; i < this.vehicles.length; i++) {
        const { _id } = this.vehicles[i];

        if (_id === vehicle._id) {
          this.vehicles[i] = this.helperService.deepCopy(vehicle);
          this.defaultVehicles[i] = this.helperService.deepCopy(vehicle);
        }
      }
    }
  }

  handleCloseSidebar() {
    this.onSidebarOutside();
  }

  onSidebarOutside() {
    if (this.isFinalizedRo) {
      this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
      return;
    }
    let hasUnsavedChanges = false;
    if (this.dataService.isMyFleetCenterEnabled && this.dataService.isMyFleetCenterConnected) {
      this.isDriverUpdated = this.getDriverChanges();
    }
    this.isVehicleUpdated = !isEqual(this.vehicles, this.defaultVehicles) && !this.isVehiclesLoading;
    if (this.isTransferVehicleEnabled) {
      const vehicles = [];
      this.vehicles.forEach((vehicle) => {
        if (vehicle.isTransferActive) {
          if (this.transferVehicles.length) {
            vehicle = this.transferVehicles.filter((defaultVehicle) => defaultVehicle._id === vehicle._id)[0];
          }
        }
        vehicles.push(vehicle);
      });
      this.vehicles = vehicles;
    }

    this.customer.phone = this.helperService.removePhoneFormatting(this.customer.phone || '');
    this.defaultCustomer.phone = this.helperService.removePhoneFormatting(this.defaultCustomer.phone || '');

    if (!this.customer.hasNoChanges) {
      this.isCustomerUpdated = !isEqual(this.customer, this.defaultCustomer) && !this.isCustomerLoading;
    }

    hasUnsavedChanges = this.isVehicleUpdated || this.isCustomerUpdated || this.isDriverUpdated;

    if (this.openNotesTab) {
      if (this.notePanelData.notes != this.defaultNotes) {
        hasUnsavedChanges = true;
      } else {
        hasUnsavedChanges = false;
      }
    }

    if (hasUnsavedChanges) {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          title: 'Save changes?',
          message: 'You have unsaved changes in the panel. Click save to update changes.',
          type: 'changes',
          button: {
            success: { title: 'Save' },
            discard: { title: 'Dismiss' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
        if (isSuccess) {
          if (this.isCustomerUpdated) {
            this.dataService.sendData(this.dataService.typePassData('sidebar-customer-update', {}));
          }

          if (this.isVehicleUpdated) {
            this.dataService.sendData(this.dataService.typePassData('sidebar-vehicle-update', {}));
          }

          if (this.isDriverUpdated) {
            this.updateBulkDrivers();
          }

          if (this.openNotesTab) {
            this.dataService.sendData(this.dataService.typePassData('save-item-description', { ...this.notePanelData, isSuccess }));
            this.dataService.sendData(this.dataService.typePassData('save-notes', { ...this.notePanelData, isSuccess }));
            this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
          }
        } else {
          if (data.btn !== 'cancel' && !this.openNotesTab) {
            this.handleResetForm();
            this.handleUnsubscribleClickoutside();
            this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
          }

          if (this.openNotesTab) {
            this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
          }
        }

        onClose.unsubscribe();
        setTimeout(() => {
          this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
        }, 1000);
      });
    } else {
      this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
    }
  }

  updateVehicleConnectedStatus() {
    this.openConnectMotor = false;
  }

  navigateToCustomerCenter() {
    this.helperService.openPageInNewExistingTab(`/customer/customer-center/${this.customer._id}`, this.customer._id);
  }

  navigateToChat() {
    this.customerData;

    let isPrimaryMobile = false;
    let primaryContact = '';
    let contactNumber = '';

    this.customerData.contact.contactInfo.map((contact) => {
      if ((contact.contactType == 'mobile' || (contact.contactType && contact.contactType.value == 'mobile')) && contact.isPrimary == true && contact.value != '') {
        isPrimaryMobile = true;
        primaryContact = contact._id;
        contactNumber = contact.value;
      }
    });

    if (isPrimaryMobile) {
      this.dataService.customerData = {
        customerId: this.customerData.contact.contactEntityId,
        contactId: primaryContact,
        name: this.customer.fullName,
        contact: contactNumber,
      };
      setTimeout(() => {
        this.handleCloseSidebar();
      }, 0);
      /*
    NOTE: Here we are decoding the customerId because we want to open chat in new existing tab
    if customerId is opened in tab and if we hit chat button so it would not open in new tab thats why we use decoding
   */
      this.helperService.openPageInNewExistingTab(`chat?customerId=${btoa(this.customerData.contact.contactEntityId)}`, btoa(this.customerData.contact.contactEntityId));
    } else {
      this.helperService.showToaster('error', 'Customer does not have a mobile number');
    }
  }

  handleFocus(type: string) {
    this.acFocus[type] = true;
    this.errors[type] = '';
    if (type === 'notes') {
      this.notesRows = 3;
    }
  }

  handleChange(name: string, value: string) {
    this.notePanelData[name] = value;
  }

  saveNotes() {
    try {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          styleClassHeading: 'font-14',
          title: 'Save Changes',
          message: `The notes will be added to RO.`,
          button: {
            success: { title: 'Confirm' },
            cancel: { title: 'Cancel' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
          this.dataService.sendData(this.dataService.typePassData('save-notes', { ...this.notePanelData, isSuccess }));
          this.dataService.sendData(this.dataService.typePassData('save-item-description', { ...this.notePanelData, isSuccess }));
          // this.handleCloseSidebar();
        }
        onClose.unsubscribe();
      });
    } catch (error) {
      this.helperService.showToaster('error', error);
    }
  }

  setSidebarZIndex() {
    try {
      setTimeout(() => {
        const sidebar = document.querySelector('#customer-sidebar');
        const dialogs = document.querySelectorAll('p-dynamicdialog');
        let highestZIndex = 0;
        if (dialogs && dialogs.length) {
          dialogs.forEach((dialog: any) => {
            const style = dialog.children && dialog.children[0] && dialog.children[0].style;
            if (style.zIndex && Number(style.zIndex) > highestZIndex) {
              highestZIndex = Number(style.zIndex);
            }
          });
        }
        if (highestZIndex) {
          const sidebarContainer: any = sidebar.querySelector('.p-sidebar');
          if (sidebarContainer) {
            const sidebarOverlay: any = document.querySelector('.p-sidebar-mask');
            sidebarOverlay.style.zIndex = highestZIndex;
            sidebarContainer.style.zIndex = `${highestZIndex + 1}`;
          }
        }
      }, 100);
    } catch (err) {
      console.log(err);
    }
  }

  getAllCreditMemo(customerId, isScroll = false) {
    if (isScroll) {
      this.creditMemoSkip += 20;
    } else {
      this.creditMemo = [];
    }

    const queryParams = {
      limit: 20,
      skip: this.creditMemoSkip,
      isPagination: true,
      isApplyPayment: false,
      customerId,
    };
    this.httpService
      .getCreditMemoList(queryParams)
      .then((res: any) => {
        const creditMemo = res?.records;
        this.totalCreditMemoRecords = res?.metaData?.totalRecords;

        if (creditMemo && creditMemo.length > 0) {
          this.creditMemo = [...this.creditMemo, ...this.formatService.formatCustomerCreditMemo(creditMemo)];
          this.totalAvailableCredit = this.customer.creditAmount;
        }
      })
      .catch((err) => this.helperService.showToaster('error', err));
  }

  handleFinanceTab(tab) {
    if ((tab === 1) || (tab === 2 && !this.customer.chargeAccountEnable)) return;
    this.financeTab = tab;
  }

  openConfirmationDialog() {
    let ref: DynamicDialogRef;
    if (!this.isOpenConfirmationDialog) {
      ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '35%',
        styleClass: 'add-dialog charge-account-dialog',
        showHeader: false,
        data: {
          isCustomizedConfirmationBox: true,
          isHTML: true,
          styleClassHeading: 'font-18 font-700',
          title: `Open a charge account`,
          message: `<div>
                      <p class="m-0 font-secondary font-12 mb-2">
                        Opening a charge account for this customer would mean:
                      </p>
                      <p class="m-0 font-16">
                       <ul>
                        <li>You can maintain an AR balance for them.</li>
                        <li>You'll be able to send bulk statments to them.</li></ul>
                        </p>

                           <p class="m-0 font-secondary font-12 mt-2 mb-2">
                      This action is <strong>irreversible</strong>. Once a charge account is opened, the customer account cannot be closed.
                    </p>

                      <p class="m-0 font-secondary font-12 mb-2">
                        Would you like to proceed?
                      </p>
                    </div>`,
          button: {
            success: { title: 'Confirm' },
            cancel: { title: 'Cancel' },
          },
        },
      });
    }

    this.isOpenConfirmationDialog = true;

    const onClose = ref?.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        const data = this.customer;
        this.dataService.sendData(this.dataService.typeSidebar('close', null, null));
        if (data && data?._id) {
          this.dataService.sendData(this.dataService.typePassData('update-customer-charge-account', {}));
          this.dataService.sendData(this.dataService.typePassData('ar-account-limit-updated', {}));
          this.dataService.sendData(this.dataService.typePassData('sent-to-ar', data));
        }
      } else {
        this.customer.chargeAccountEnable = false;
      }
      this.isOpenConfirmationDialog = false;
      onClose.unsubscribe();
    });
  }

  async getAllDrivers() {
    try {
      const drivers = await this.httpService.getCustomerDrivers(this.customerId);

      drivers.map((driver) => {
        if (!driver.isActive) driver.disabled = true;

        if (this.selectedDriverId) {
          driver.showForm = driver._id === this.selectedDriverId;
        }
      });

      this.drivers = drivers;
      this.defaultDrivers = this.helperService.deepCopy(drivers); // Deep copy to maintain original state
      this.vehicleDrivers = this.helperService.deepCopy(drivers); //Fix for the ALP-18771

    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  }

  async getAllDriversForVehicles() {
    try {
      const vehicleDrivers = await this.httpService.getCustomerDrivers(this.customerId);

      vehicleDrivers.forEach((driver) => {
        if (!driver.isActive) driver.disabled = true;
      });

      this.vehicleDrivers = vehicleDrivers;
    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  }

  updateBulkDrivers() {
    const drivers = this.drivers.reduce((acc, driver) => {
      // Delete _id if it's an empty string
      if (driver._id === '') {
        delete driver._id;
      }

      // If fullName is missing, skip this driver and show an error
      if (!driver.fullName) {
        this.helperService.showToaster('error', 'Driver fullName is required');
        return acc; // Skip this driver
      }

      // Add the driver with default values for missing keys
      acc.push({
        ...driver,
        employeeNumber: driver.employeeNumber || '',
        serviceCardNumber: driver.serviceCardNumber || '',
      });

      return acc;
    }, []);

    this.httpService
      .updateBulkDrivers({
        drivers: drivers,
        customerId: this.customer._id,
      })
      .then()
      .catch((err) => {
        this.helperService.showToaster('error', err);
      });
  }

  deleteDriversShowForm() {
    const drivers = this.helperService.deepCopy(this.drivers);
    for (let i = 0; i < drivers.length; i++) {
      delete drivers[i].showForm;
    }
    return drivers;
  }

  // Helper function to check if the driver object is empty
  isDriverFormEmpty(driver: IDriver): boolean {
    // Check if all relevant fields are empty
    return (
      !driver.fullName &&
      !driver.email &&
      !driver.phone &&
      !driver.employeeNumber &&
      !driver.serviceCardNumber &&
      !driver.companyName &&
      !driver.department &&
      !driver.address.addLine1 &&
      !driver.address.city &&
      !driver.address.state &&
      !driver.address.zip &&
      !driver.address.country
    );
  }

  extractRelevantFields(driver: IDriver) {
    return {
      fullName: driver.fullName,
      email: driver.email,
      phone: driver.phone,
      employeeNumber: driver.employeeNumber,
      serviceCardNumber: driver.serviceCardNumber,
      companyName: driver.companyName,
      department: driver.department,
      isActive: driver.isActive,
      _id: driver._id,
      address: {
        addLine1: driver.address.addLine1,
        city: driver.address.city,
        state: driver.address.state,
        zip: driver.address.zip,
        country: driver.address.country,
      },
    };
  }

  getDriverChanges() {
    const drivers = this.deleteDriversShowForm();
    // Filter drivers to exclude empty forms
    const filteredDrivers = drivers.filter((driver) => !this.isDriverFormEmpty(driver));
    // Extract relevant fields from both defaultDrivers and filteredDrivers for accurate comparison
    const filteredDefaultDrivers = this.defaultDrivers.map(this.extractRelevantFields);
    const filteredUpdatedDrivers = filteredDrivers.map(this.extractRelevantFields);
    // Compare the filtered drivers to determine if there are any updates
    const isDriverUpdated = !isEqual(filteredDefaultDrivers, filteredUpdatedDrivers);

    return isDriverUpdated;
  }

  setContactValidation() {
    const phone = this.customer.phone;
    const email = this.customer.email;

    this.contactValidation = {
      email: {
        tooltip: '',
        icon: '',
      },
      phone: {
        tooltip: '',
        icon: '',
      },
    };

    if (!email) {
      if (this.dataService.customerMandatoryFields.email) {
        this.contactValidation.email = {
          icon: 'fal fa-exclamation-triangle font-13 primary-contact-danger',
          tooltip: 'Email is a required field',
        };
      } else {
        this.contactValidation.email = {
          icon: 'fal fa-exclamation-triangle font-13 primary-contact',
          tooltip: 'Adding email is recommended',
        };
      }
    }

    if (!phone) {
      if (this.dataService.customerMandatoryFields.mobile) {
        this.contactValidation.phone = {
          icon: 'fal fa-exclamation-triangle font-13 primary-contact-danger',
          tooltip: 'Phone number is a required field',
        };
      } else {
        this.contactValidation.phone = {
          icon: 'fal fa-exclamation-triangle font-13 primary-contact',
          tooltip: 'Adding phone is recommended',
        };
      }
    }
  }
}
